import React from "react";
import * as Mui from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SettingsIcon from "@material-ui/icons/Settings";
import SyncIcon from "@material-ui/icons/Sync";
import { EndGunSection, Pivot } from "../../dtos/pivot";
import { PivotControlButton } from "./PivotControlButton";
import { PivotEquipmentTabs } from "./PivotEquipmentTabs";
import { PivotSetupGraphic } from "./PivotSetupGraphic";
import { useMessage } from "../messageService";

interface PivotDetailsPanelProps {
  pivot: Pivot;
  configuredEndGunSections: EndGunSection[];
  onEditPivot: () => void;
}

const formatAction = (value?: "stop" | "reverse" | "none") => {
  if (!value) {
    return "Not set";
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatUpdatedAt = (value: Date) => value.toLocaleString([], {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit"
});

const StyledCardContent = Mui.withStyles((theme: any) => ({
  root: {
    backgroundColor: theme.cardHeaderColor
  }
}))(Mui.CardContent);

const DataPair = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <>
    <Mui.Grid item xs={6} md={3}>
      <div>{label}</div>
    </Mui.Grid>
    <Mui.Grid item xs={6} md={3}>
      <div><b>{value}</b></div>
    </Mui.Grid>
  </>
);

const LiveStat = ({ label, value, accent }: { label: string; value: React.ReactNode; accent?: string }) => (
  <Mui.Grid item xs={6}>
    <div>
      <Mui.Typography variant="body2" color="textSecondary">{label}</Mui.Typography>
      <Mui.Typography variant="h6" style={accent ? { color: accent } : undefined}>
        {value}
      </Mui.Typography>
    </div>
  </Mui.Grid>
);

export const PivotDetailsPanel = ({ pivot, configuredEndGunSections, onEditPivot }: PivotDetailsPanelProps) => {
  const classes = useStyles();
  const { showMessage } = useMessage();
  const [lastUpdated, setLastUpdated] = React.useState(() => new Date());
  const statusColor = !pivot.online ? "#888" : pivot.status === "Running" ? "#4caf50" : pivot.status === "Stopped" ? "#f44336" : "#888";
  const pivotType = pivot.pivot_type ?? "full";
  const configuredSections = configuredEndGunSections ?? [];

  const handleFetchCurrentData = () => {
    setLastUpdated(new Date());
    showMessage(`Fetched current pivot data for ${pivot.name}.`);
  };

  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>
        <Mui.Grid container>
          <Mui.Grid item xs={12} md={6}>
            <div className={classes.headerSection}>
              <label>{pivot.name}</label>
              <div className={classes.serialNumber}>{pivot.serial}</div>
            </div>
          </Mui.Grid>
          <Mui.Grid item xs={12} md={6}>
            <div className={classes.headerRight}>
              <div className={classes.headerActions}>
                <PivotControlButton pivot={pivot} className={classes.autoControlButton} />
                <Mui.Button
                  variant="contained"
                  color="primary"
                  onClick={onEditPivot}
                  className={classes.pivotSettingsButton}
                  endIcon={<SettingsIcon />}
                >
                  Edit Pivot
                </Mui.Button>
              </div>
            </div>
          </Mui.Grid>
        </Mui.Grid>
      </div>

      <Mui.Card className={classes.card} variant="outlined">
        <StyledCardContent>
          <div className={classes.cardHeader}>
            <Mui.Grid container justify="space-between" alignItems="center">
              <Mui.Grid item>
                <div className={classes.cardHeaderTitle}>
                  Status <span className={classes.statusTitleValue} style={{ color: statusColor }}>{pivot.online ? pivot.status : "Offline"}</span>
                </div>
              </Mui.Grid>
              <Mui.Grid item>
                <div className={classes.cardHeaderMeta}>
                  <span>{pivotType === "full" ? "Full Circle" : "Partial Circle"}</span>
                  <span className={classes.statusDivider}>·</span>
                  <span>{pivot.travel_direction ?? "CW"}</span>
                </div>
              </Mui.Grid>
            </Mui.Grid>
          </div>
        </StyledCardContent>
        <Mui.CardContent>
          <div className={classes.liveToolbar}>
            <Mui.Typography variant="body2" color="textSecondary">
              Last Updated <b>{formatUpdatedAt(lastUpdated)}</b>
            </Mui.Typography>
            <Mui.Button
              variant="outlined"
              size="small"
              startIcon={<SyncIcon />}
              onClick={handleFetchCurrentData}
              className={classes.fetchButton}
            >
              Fetch Current Data
            </Mui.Button>
          </div>

          <Mui.Grid container spacing={3}>
            <Mui.Grid item xs={12} md={5}>
              <div className={classes.graphicWrap}>
                <PivotSetupGraphic
                  limit_angle_a={pivot.limit_angle_a ?? 0}
                  limit_angle_b={pivot.limit_angle_b ?? 359}
                  travel_direction={pivot.travel_direction ?? "CW"}
                  end_gun_enabled={pivot.end_gun_enabled ?? true}
                  end_gun_sections={configuredSections}
                  current_angle={pivot.anglePercent}
                />
              </div>
            </Mui.Grid>
            <Mui.Grid item xs={12} md={7}>
              <Mui.Grid container spacing={3}>
                <LiveStat label="Flow" value={`${pivot.flow ?? 0} gpm`} />
                <LiveStat label="Pressure" value={`${pivot.pressure ?? 0} psi`} />
                <LiveStat label="Current Angle" value={`${pivot.anglePercent}°`} />
                <LiveStat label="Speed" value={`${pivot.speed_percent ?? 0}%`} />
                <LiveStat label="Direction" value={pivot.travel_direction ?? "CW"} />
                <LiveStat label="End Gun" value={pivot.end_gun_enabled ?? true ? "Active" : "Off"} />
              </Mui.Grid>
              <div className={classes.detailsMiniSection}>
                <Mui.Typography variant="subtitle2" className={classes.detailsMiniTitle}>
                  Setup Details
                </Mui.Typography>
                <Mui.Grid container spacing={2}>
                  <Mui.Grid item xs={6}>
                    <Mui.Typography variant="body2" color="textSecondary">Length</Mui.Typography>
                    <Mui.Typography variant="body1"><b>{pivot.length} ft</b></Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid item xs={6}>
                    <Mui.Typography variant="body2" color="textSecondary">Travel Direction</Mui.Typography>
                    <Mui.Typography variant="body1"><b>{pivot.travel_direction ?? "CW"}</b></Mui.Typography>
                  </Mui.Grid>
                </Mui.Grid>
              </div>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.CardContent>
      </Mui.Card>

      <PivotEquipmentTabs pivot={pivot} />

      <Mui.Card className={classes.card} variant="outlined">
        <StyledCardContent>
          <div className={classes.cardHeader}>
            <Mui.Grid container justify="space-between" alignItems="center">
              <Mui.Grid item>
                <div className={classes.cardHeaderTitle}>End Gun Sections</div>
              </Mui.Grid>
              <Mui.Grid item>
                <div className={classes.cardHeaderMeta}>{configuredSections.length} configured</div>
              </Mui.Grid>
            </Mui.Grid>
          </div>
        </StyledCardContent>
        <Mui.CardContent>
          {configuredSections.length === 0 ? (
            <Mui.Typography variant="body2" color="textSecondary">No end gun sections configured.</Mui.Typography>
          ) : (
            <Mui.Grid container spacing={2}>
              {configuredSections.map((section, index) => (
                <Mui.Grid item xs={12} key={`${section.name ?? "section"}-${index}`}>
                  <div className={classes.endGunRow}>
                    <Mui.Typography variant="body2" className={classes.endGunSummary}>
                      <span className={classes.endGunSummaryLabel}>Section {index + 1}:</span> <b>{section.name || "Unnamed section"}</b>
                      <span className={classes.endGunSummaryDivider}>·</span>
                      {section.enabled ? "Enabled" : "Disabled"}
                      <span className={classes.endGunSummaryDivider}>·</span>
                      {section.limit_angle_1}° to {section.limit_angle_2}°
                    </Mui.Typography>
                  </div>
                </Mui.Grid>
              ))}
            </Mui.Grid>
          )}
        </Mui.CardContent>
      </Mui.Card>

      <Mui.Card className={classes.card} variant="outlined">
        <StyledCardContent>
          <div className={classes.cardHeader}>
            <Mui.Grid container justify="space-between" alignItems="center">
              <Mui.Grid item>
                <div className={classes.cardHeaderTitle}>Operating Limits</div>
              </Mui.Grid>
            </Mui.Grid>
          </div>
        </StyledCardContent>
        <Mui.CardContent>
          <Mui.Grid container spacing={2}>
            <DataPair label="Limit Angle A" value={`${pivot.limit_angle_a ?? 0}°`} />
            <DataPair label="At Limit A" value={formatAction(pivot.limit_a_action)} />
            <DataPair label="Limit Angle B" value={`${pivot.limit_angle_b ?? 359}°`} />
            <DataPair label="At Limit B" value={formatAction(pivot.limit_b_action)} />
          </Mui.Grid>
        </Mui.CardContent>
      </Mui.Card>
    </div>
  );
};

const useStyles = makeStyles((theme: any) => ({
  root: {
    width: "95%",
    marginBottom: theme.standardMargin
  },
  sectionHeader: {
    color: theme.selectedItemColor,
    justifySelf: "start",
    fontSize: "1.25em",
    fontWeight: "bold",
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    marginBottom: theme.standardMargin
  },
  headerSection: {
    display: "flex",
    maxWidth: "900px",
    marginBottom: theme.standardMargin,
    justifySelf: "center",
    alignItems: "center",
    "& div": {
      marginLeft: ".5em"
    }
  },
  headerRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginBottom: theme.standardMargin,
    [theme.breakpoints.down("sm")]: {
      alignItems: "center"
    }
  },
  headerActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "& button": {
      marginLeft: ".5em"
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "center",
      flexWrap: "wrap",
      "& button": {
        marginBottom: theme.spacing(1)
      }
    }
  },
  serialNumber: {
    color: theme.textColor,
    fontSize: "1em",
    fontWeight: "normal"
  },
  card: {
    width: "100%",
    marginBottom: theme.spacing(2)
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center"
  },
  cardHeaderTitle: {
    fontWeight: "bold"
  },
  statusTitleValue: {
    marginLeft: theme.spacing(1)
  },
  cardHeaderMeta: {
    color: theme.textColor,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1)
    }
  },
  statusDivider: {
    margin: `0 ${theme.spacing(1)}px`
  },
  liveToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  fetchButton: {
    textTransform: "none"
  },
  autoControlButton: {
    backgroundColor: theme.mainBackgroundColor,
    color: theme.selectedItemColor,
    textTransform: "none"
  },
  pivotSettingsButton: {
    backgroundColor: theme.selectedItemColor,
    color: theme.whiteColor,
    textTransform: "none"
  },
  graphicWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 280,
    transform: "scale(0.9)",
    transformOrigin: "center center"
  },
  detailsMiniSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.cardHeaderColor,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  detailsMiniTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1.5),
    color: theme.textColor
  },
  endGunRow: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  endGunSummary: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap"
  },
  endGunSummaryLabel: {
    color: theme.textColor
  },
  endGunSummaryDivider: {
    margin: `0 ${theme.spacing(1)}px`,
    color: theme.textColor
  }
}));
